import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import ScannerIcon from "@mui/icons-material/QrCodeScanner";
import productsService from "../../../services/productsService";

const ModalPrepared = ({
  ingressDetail,
  open,
  onClose,
  onConfirm,
  loading,
  shippingMethod,
  scanOnPrepare = true,
}) => {
  // Modo sin escáner (preferencia de la cuenta): el operario marca cada producto
  // como completo con un clic en el checkbox, en vez de escanear SKU/EAN.
  const handleManualToggle = (product) => {
    const isCurrentlyComplete = selectedProducts.some((p) => p.sku_id === product.sku_id);
    if (isCurrentlyComplete) {
      setSelectedProducts((prev) => prev.filter((p) => p.sku_id !== product.sku_id));
      setProductScanCounts((prev) => ({ ...prev, [product.sku_id]: 0 }));
    } else {
      setProductScanCounts((prev) => ({ ...prev, [product.sku_id]: product.original_count }));
      setSelectedProducts((prev) =>
        prev.some((p) => p.sku_id === product.sku_id) ? prev : [...prev, product]
      );
    }
  };
  // Solo la paquetería (envío convencional) necesita las medidas de cada bulto
  // para cotizar. El resto de operativas solo requiere la cantidad de bultos.
  const isPaqueteria = shippingMethod === "red_despacho";
  // ME2/Flex (Camino A): ML ya genera la etiqueta → no se piden bultos; el operario
  // solo escanea los productos y confirma. package_count se asume 1.
  const isMlLabeled = shippingMethod === "me2" || shippingMethod === "me_flex";
  const [scannedData, setScannedData] = useState("");
  const [lastScannedItem, setLastScannedItem] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [error, setError] = useState("");
  const [cantidadError, setCantidadError] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [productScanCounts, setProductScanCounts] = useState({});
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [autoScannedProducts, setAutoScannedProducts] = useState(new Set());
  const [expandedDetails, setExpandedDetails] = useState([]);
  const [loadingExpansion, setLoadingExpansion] = useState(false);
  const quantityRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const productInputRef = useRef(null);
  const bultoCountRef = useRef(null);

  // Bultos: los carga el operario en la packing station (no en la creación).
  const [bultoCount, setBultoCount] = useState("");
  const [packages, setPackages] = useState([]);
  // Evita que el listener global del lector de código de barras robe las teclas
  // mientras el operario tipea en los campos de bultos.
  const [isPackagesFocused, setIsPackagesFocused] = useState(false);

  const handleBultoCountChange = (val) => {
    const digits = val.replace(/\D/g, "");
    setBultoCount(digits);
    if (digits === "") {
      setPackages([]);
      return;
    }
    const n = parseInt(digits, 10);
    if (Number.isNaN(n) || n <= 0 || n > 50) return;
    setPackages((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) {
        next.push({ width: "", height: "", depth: "", total_weight: "", declared_value: "" });
      }
      return next;
    });
  };

  const updatePackageField = (idx, field, val) => {
    const clean = val.replace(/[^\d.]/g, "");
    setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: clean } : p)));
  };

  const packagesValid =
    isMlLabeled ||
    // Envío convencional: los bultos ya se cargaron en la creación; acá no se piden ni
    // se validan (el operario solo escanea). Los bultos se preservan desde el egreso.
    isPaqueteria ||
    (packages.length > 0 &&
      (!isPaqueteria ||
        packages.every(
          (p) =>
            Number(p.width) > 0 &&
            Number(p.height) > 0 &&
            Number(p.depth) > 0 &&
            Number(p.total_weight) > 0
        )));

  const buildPackagesPayload = () => {
    // ME2/Flex: sin bultos cargados → 1 bulto por default (ML define el paquete).
    if (isMlLabeled) {
      return [{ index: 1, width: 0, height: 0, depth: 0, declared_value: 0, total_weight: 0 }];
    }
    return packages.map((p, i) => ({
      index: i + 1,
      width: Number(p.width) || 0,
      height: Number(p.height) || 0,
      depth: Number(p.depth) || 0,
      declared_value: Number(p.declared_value) || 0,
      total_weight: Number(p.total_weight) || 0,
    }));
  };

  // Expand combo products into their individual components
  useEffect(() => {
    if (!open) {
      return;
    }

    // Reset form first
    setLastScannedItem(null);
    setScannedData("");
    setSelectedProducts([]);
    setError("");
    setCantidadError("");
    setCantidad("");
    setProductScanCounts({});
    setSuccessMessage("");
    setAutoScannedProducts(new Set());
    setExpandedDetails([]);
    setLoadingExpansion(false);

    if (!Array.isArray(ingressDetail) || ingressDetail.length === 0) {
      return;
    }

    let cancelled = false;
    const expandCombos = async () => {
      setLoadingExpansion(true);
      const expanded = [];

      for (const detail of ingressDetail) {
        try {
          const resp = await productsService.getProductsBySku(detail.product_id);
          const productData = resp?.data?.[0] || resp?.data || resp?.[0] || resp;

          if (productData?.is_combo && Array.isArray(productData.combo) && productData.combo.length > 0) {
            // Fetch each component's data
            for (const component of productData.combo) {
              try {
                const compResp = await productsService.getProductsBySku(component.product_id);
                const compData = compResp?.data?.[0] || compResp?.data || compResp?.[0] || compResp;

                expanded.push({
                  sku_id: compData?.sku_id || `COMP-${component.product_id}`,
                  ean_id: compData?.ean_id || "",
                  description: compData?.name || `Componente ${component.product_id}`,
                  original_count: component.quantity * detail.original_count,
                  product_id: component.product_id,
                  _parent_sku: detail.sku_id,
                  _is_combo_component: true,
                });
              } catch (err) {
                console.error(`Error fetching component ${component.product_id}:`, err);
                expanded.push({
                  sku_id: `COMP-${component.product_id}`,
                  ean_id: "",
                  description: `Componente ${component.product_id}`,
                  original_count: component.quantity * detail.original_count,
                  product_id: component.product_id,
                  _parent_sku: detail.sku_id,
                  _is_combo_component: true,
                });
              }
            }
          } else {
            // Not a combo — keep as-is
            expanded.push({ ...detail });
          }
        } catch (err) {
          console.error(`Error fetching product ${detail.product_id}:`, err);
          // Fallback: keep original detail
          expanded.push({ ...detail });
        }
      }

      if (!cancelled) {
        // Aggregate by sku_id: if the same component appears from multiple combos
        // or also as a standalone product, merge them into one line
        const aggregated = [];
        const skuMap = {};
        for (const item of expanded) {
          if (skuMap[item.sku_id]) {
            skuMap[item.sku_id].original_count += item.original_count;
          } else {
            skuMap[item.sku_id] = { ...item };
            aggregated.push(skuMap[item.sku_id]);
          }
        }
        setExpandedDetails(aggregated);
        setLoadingExpansion(false);
      }
    };

    expandCombos();

    return () => {
      cancelled = true;
    };
  }, [open, ingressDetail]);

  const resetForm = () => {
    setLastScannedItem(null);
    setScannedData("");
    setSelectedProducts([]);
    setError("");
    setCantidadError("");
    setCantidad("");
    setProductScanCounts({});
    setSuccessMessage("");
    setAutoScannedProducts(new Set());
    setExpandedDetails([]);
    setLoadingExpansion(false);
    setBultoCount("");
    setPackages([]);
  };

  const resetCurrentScan = () => {
    setLastScannedItem(null);
    setScannedData("");
    setCantidad("");
    setError("");
    setCantidadError("");
    setSuccessMessage("");
  };

  const validateQuantity = (value, product) => {
    if (!product) return true;

    const currentCount = productScanCounts[product.sku_id] || 0;
    const remainingCount = product.original_count - currentCount;
    const numValue = parseInt(value);

    if (numValue > remainingCount) {
      setCantidadError(`Cantidad máxima ${remainingCount}`);
      return false;
    }

    setCantidadError("");
    return true;
  };

  const updateProductScanCount = (product, manualCount) => {
    if (!manualCount || manualCount <= 0) {
      setCantidadError("La cantidad debe ser mayor a 0");
      return false;
    }

    const currentCount = productScanCounts[product.sku_id] || 0;
    const targetCount = product.original_count;

    if (currentCount + parseInt(manualCount) > targetCount) {
      setCantidadError(`La cantidad excede el límite permitido`);
      return false;
    }

    const newCount = Math.min(
      currentCount + parseInt(manualCount),
      targetCount
    );

    setProductScanCounts((prev) => ({
      ...prev,
      [product.sku_id]: newCount,
    }));

    if (newCount >= targetCount) {
      setSelectedProducts((prev) => {
        if (!prev.some((p) => p.sku_id === product.sku_id)) {
          return [
            ...prev,
            {
              sku_id: product.sku_id,
              name: product.description,
            },
          ];
        }
        return prev;
      });
    } else {
      setSelectedProducts((prev) =>
        prev.filter((p) => p.sku_id !== product.sku_id)
      );
    }

    return true;
  };

  const findProduct = (searchValue) => {
    return expandedDetails?.find(
      (product) =>
        product.sku_id === searchValue || product.ean_id === searchValue
    );
  };

  const getProgressMessage = (product) => {
    const currentCount = productScanCounts[product.sku_id] || 0;
    const remaining = product.original_count - currentCount;

    if (remaining === 0) {
      return "¡Producto completado!";
    } else if (remaining === 1) {
      return `¡Solo falta 1 más!`;
    } else {
      return `Faltan ${remaining} de ${product.original_count}`;
    }
  };

  // Función para calcular el progreso de productos completados
  const getCompletedProductsProgress = () => {
    const totalProducts = expandedDetails?.length || 0;
    const completedProducts = selectedProducts.length;
    const progressPercentage =
      totalProducts > 0 ? (completedProducts / totalProducts) * 100 : 0;

    return {
      completed: completedProducts,
      total: totalProducts,
      percentage: progressPercentage,
    };
  };

  const handleBarcodeScan = (event) => {
    if (isQuantityFocused) {
      return;
    }

    if (
      event.key === "Tab" ||
      event.key === "Shift" ||
      event.key === "Control" ||
      event.key === "Alt"
    ) {
      return;
    }

    event.preventDefault();

    if (event.key === "Enter") {
      if (scannedData.trim()) {
        const existingProduct = findProduct(scannedData.trim());

        if (existingProduct) {
          setLastScannedItem(existingProduct);
          setError("");

          const currentCount = productScanCounts[existingProduct.sku_id] || 0;
          const remaining = existingProduct.original_count - currentCount;

          if (remaining > 0) {
            const newCount = currentCount + 1;
            setProductScanCounts((prev) => ({
              ...prev,
              [existingProduct.sku_id]: newCount,
            }));

            setAutoScannedProducts(
              (prev) => new Set([...prev, existingProduct.sku_id])
            );

            if (newCount >= existingProduct.original_count) {
              setSelectedProducts((prev) => {
                if (!prev.some((p) => p.sku_id === existingProduct.sku_id)) {
                  return [
                    ...prev,
                    {
                      sku_id: existingProduct.sku_id,
                      name: existingProduct.description,
                    },
                  ];
                }
                return prev;
              });
            }

            setSuccessMessage(
              `✅ +1 ${existingProduct.description} (${newCount}/${existingProduct.original_count})`
            );
          } else {
            setSuccessMessage("⚠️ Este producto ya está completo");
          }
        } else {
          setError(`❌ Producto no encontrado: ${scannedData}`);
          setLastScannedItem(null);
        }
      }
      setScannedData("");
    } else if (event.key === "Backspace") {
      setScannedData((prev) => prev.slice(0, -1));
    } else if (event.key.length === 1) {
      setScannedData((prev) => prev + event.key.toUpperCase());
    }
  };

  const handleAddProduct = () => {
    if (!lastScannedItem) {
      setError("Debe escanear un producto primero");
      return;
    }

    if (updateProductScanCount(lastScannedItem, cantidad)) {
      setSuccessMessage(
        `✅ Agregado: ${cantidad} x ${lastScannedItem.description}`
      );
      if (quantityRef.current) {
        quantityRef.current.blur();
      }
      resetCurrentScan();
      // Re-focus product input for next scan
      setTimeout(() => {
        if (productInputRef.current) {
          productInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleManualInput = (event) => {
    setScannedData(event.target.value.toUpperCase());
    setError("");
    setSuccessMessage("");
  };

  const allComplete = expandedDetails.length > 0 && selectedProducts.length === expandedDetails.length;

  // Al completar todos los productos: en modo sin escáner, si faltan bultos enfoca
  // el campo de bultos; si no, el botón Confirmar (igual que el flujo con escáner).
  useEffect(() => {
    if (!allComplete) return;
    setTimeout(() => {
      setIsQuantityFocused(false);
      if (!scanOnPrepare && !packagesValid && bultoCountRef.current) {
        bultoCountRef.current.focus();
      } else if (confirmButtonRef.current) {
        confirmButtonRef.current.focus();
      }
    }, 150);
  }, [allComplete, scanOnPrepare, packagesValid]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Escape cierra el modal en cualquier modo.
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      // Modo SIN escáner: navegación por Enter → productos → bultos → confirmar.
      if (!scanOnPrepare) {
        if (isPackagesFocused) return; // el campo de bultos maneja su propio Enter
        if (event.key === "Enter" && !loading) {
          event.preventDefault();
          if (!allComplete) {
            const next = expandedDetails.find(
              (p) => !selectedProducts.some((s) => s.sku_id === p.sku_id)
            );
            if (next) handleManualToggle(next);
          } else if (!packagesValid) {
            if (bultoCountRef.current) bultoCountRef.current.focus();
          } else {
            onConfirm(buildPackagesPayload());
          }
        }
        return;
      }

      if (isQuantityFocused || isPackagesFocused) {
        return;
      }

      // When all products are scanned, Enter triggers confirm
      if (allComplete && event.key === "Enter" && !loading) {
        event.preventDefault();
        onConfirm();
        return;
      }

      handleBarcodeScan(event);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    scannedData,
    expandedDetails,
    cantidad,
    isQuantityFocused,
    isPackagesFocused,
    productScanCounts,
    allComplete,
    loading,
    onConfirm,
    onClose,
    scanOnPrepare,
    packagesValid,
    selectedProducts,
  ]);

  const getScanProgress = (product) => {
    const scanned = productScanCounts[product.sku_id] || 0;
    return `${scanned}/${product.original_count}`;
  };

  const progress = getCompletedProductsProgress();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="none"
      PaperProps={{
        sx: {
          width: 1000,
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#E8F0FE",
            }}
          >
            <ScannerIcon sx={{ color: "primary.main" }} />
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Confirmar pedido
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {loadingExpansion ? (
          <Box display="flex" alignItems="center" gap={2} p={3}>
            <CircularProgress size={24} />
            <Typography variant="body1">Cargando componentes de productos combo...</Typography>
          </Box>
        ) : (
        <>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">Producto</Typography>
            <TextField
              value={scannedData}
              onChange={handleManualInput}
              placeholder="Escanea o ingresa SKU/EAN"
              fullWidth
              error={!!error}
              helperText={error}
              size="small"
              inputRef={productInputRef}
              autoFocus={scanOnPrepare}
              onKeyDown={(e) => {
                if (!scanOnPrepare) return; // modo manual: no interferir con la navegación por Enter
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  const value = scannedData.trim();
                  if (value) {
                    const existingProduct = findProduct(value);
                    if (existingProduct) {
                      setLastScannedItem(existingProduct);
                      setError("");
                      const currentCount = productScanCounts[existingProduct.sku_id] || 0;
                      const remaining = existingProduct.original_count - currentCount;
                      if (remaining > 0) {
                        setSuccessMessage(`📦 ${existingProduct.description} — Ingresá la cantidad (${remaining} restantes)`);
                        setTimeout(() => {
                          if (quantityRef.current) {
                            quantityRef.current.focus();
                          }
                        }, 50);
                      } else {
                        setSuccessMessage("⚠️ Este producto ya está completo");
                      }
                    } else {
                      setError(`❌ Producto no encontrado: ${value}`);
                      setLastScannedItem(null);
                    }
                    setScannedData("");
                  }
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  "& fieldset": {
                    borderColor: "#418fdb",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                },
                "& .MuiFormHelperText-root": {
                  marginTop: "2px",
                },
              }}
              inputProps={{
                style: {
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  color: "#2563EB",
                },
              }}
            />
          </Grid>
          <Grid item xs={1}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">Cantidad</Typography>
            <TextField
              value={cantidad}
              placeholder=" "
              fullWidth
              disabled={!lastScannedItem}
              error={!!cantidadError}
              helperText={cantidadError}
              size="small"
              inputRef={quantityRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddProduct();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                },
                "& .MuiFormHelperText-root": {
                  marginTop: "2px",
                },
                "& .Mui-error": {
                  margin: "0px",
                },
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onFocus={() => setIsQuantityFocused(true)}
              onBlur={() => setIsQuantityFocused(false)}
              onChange={(e) => {
                const value = e.target.value;
                const regex = /^[0-9]*$/;

                if (regex.test(value) || value === "") {
                  if (validateQuantity(value, lastScannedItem)) {
                    setCantidad(value);
                  } else {
                    setCantidad(value);
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Typography variant="caption">&nbsp;</Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={!lastScannedItem || !cantidad || !!cantidadError}
              onClick={handleAddProduct}
              fullWidth
              sx={{
                height: "40px",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Agregar
            </Button>
          </Grid>
          <Grid item xs={12}>
            {lastScannedItem && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "rgba(25, 118, 210, 0.08)",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  📦 {lastScannedItem.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getProgressMessage(lastScannedItem)}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          sx={{
            color:
              progress.completed === progress.total && progress.total > 0
                ? "#388e3c"
                : "black",
            fontWeight: 600,
            fontSize: "0.95rem",
          }}
        >
          SKUs escaneados: {progress.completed}/{progress.total}
        </Typography>
        </>
        )}
      </DialogContent>

      <Box sx={{ px: 3, pb: 1 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
      <Table size="small">
        <TableHead>
          <TableRow>
            {["SKU", "EAN", "Descripción", "Cantidad", "Escaneado", "Completo"].map((header, i) => (
              <TableCell
                key={header}
                align={i >= 3 ? "center" : "left"}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  color: "#475569",
                  backgroundColor: "#f1f5f9",
                  borderBottom: "2px solid #e2e8f0",
                  py: 1.5,
                  ...(header === "Descripción" ? { width: 400 } : {}),
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(expandedDetails) &&
            expandedDetails.map((producto, index) => {
              const isScanned = productScanCounts[producto.sku_id] > 0;
              const isComplete = selectedProducts.some(
                (p) => p.sku_id === producto.sku_id
              );
              const isAutoScanned = autoScannedProducts.has(producto.sku_id);

              return (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor:
                      lastScannedItem?.sku_id === producto.sku_id
                        ? "rgba(25, 118, 210, 0.08)"
                        : isComplete
                          ? "rgba(76, 175, 80, 0.08)"
                          : isScanned
                            ? "rgba(255, 193, 7, 0.08)"
                            : "inherit",
                    "&:hover": {
                      backgroundColor:
                        lastScannedItem?.sku_id === producto.sku_id
                          ? "rgba(25, 118, 210, 0.12)"
                          : isComplete
                            ? "rgba(76, 175, 80, 0.12)"
                            : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <TableCell sx={{ py: 1.2 }}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        backgroundColor: "#E8F0FE",
                        color: "#3949ab",
                        px: 1.2,
                        py: 0.3,
                        borderRadius: "6px",
                      }}
                    >
                      {producto.sku_id}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Typography variant="body2" color="text.secondary">{producto.ean_id}</Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={500}>{producto.description}</Typography>
                      {producto._is_combo_component && (
                        <Chip
                          label={`Combo: ${producto._parent_sku}`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ ml: 1, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {producto.original_count}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={productScanCounts[producto.sku_id] || 0}
                      size="small"
                      color={
                        isComplete
                          ? "success"
                          : isScanned
                            ? "primary"
                            : "default"
                      }
                      variant={isScanned ? "filled" : "outlined"}
                      sx={{
                        "& .MuiChip-label": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      disabled={scanOnPrepare}
                      checked={isComplete}
                      onChange={() => handleManualToggle(producto)}
                      sx={{
                        cursor: scanOnPrepare ? "default" : "pointer",
                        color: isComplete ? "#4caf50" : "#d0d0d0",
                        "&.Mui-checked": {
                          color: "#4caf50",
                        },
                        "&.Mui-disabled": {
                          color: isComplete ? "#4caf50" : "#d0d0d0",
                        },
                        "&.Mui-checked.Mui-disabled": {
                          color: "#4caf50",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.5rem",
                        },
                        opacity: 1,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      </Paper>
      </Box>

      {/* red_despacho: los bultos vienen de la creación → no se muestran acá. */}
      {!isMlLabeled && !isPaqueteria && (
      <Box sx={{ px: 3, pb: 1 }}>
        <Paper elevation={0} sx={{ borderRadius: "10px", border: "1px solid #e2e8f0", p: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Bultos del envío</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            {isPaqueteria
              ? "Cargá en cuántos bultos va el pedido y las medidas de cada uno (necesarias para cotizar la paquetería)."
              : "Cargá en cuántos bultos va el pedido."}
          </Typography>
          <TextField
            label="Cantidad de bultos"
            value={bultoCount}
            onChange={(e) => handleBultoCountChange(e.target.value)}
            onFocus={() => setIsPackagesFocused(true)}
            onBlur={() => setIsPackagesFocused(false)}
            inputRef={bultoCountRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && packagesValid && confirmButtonRef.current) {
                e.preventDefault();
                confirmButtonRef.current.focus();
              }
            }}
            size="small"
            sx={{ mb: 2, width: 200 }}
            inputProps={{ inputMode: "numeric" }}
          />
          {isPaqueteria &&
            packages.map((pkg, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={12} sm={2}>
                  <Typography variant="body2" fontWeight={600}>Bulto {idx + 1}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Ancho (cm)" value={pkg.width} onChange={(e) => updatePackageField(idx, "width", e.target.value)} onFocus={() => setIsPackagesFocused(true)} onBlur={() => setIsPackagesFocused(false)} size="small" fullWidth />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Alto (cm)" value={pkg.height} onChange={(e) => updatePackageField(idx, "height", e.target.value)} onFocus={() => setIsPackagesFocused(true)} onBlur={() => setIsPackagesFocused(false)} size="small" fullWidth />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Prof. (cm)" value={pkg.depth} onChange={(e) => updatePackageField(idx, "depth", e.target.value)} onFocus={() => setIsPackagesFocused(true)} onBlur={() => setIsPackagesFocused(false)} size="small" fullWidth />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Peso (kg)" value={pkg.total_weight} onChange={(e) => updatePackageField(idx, "total_weight", e.target.value)} onFocus={() => setIsPackagesFocused(true)} onBlur={() => setIsPackagesFocused(false)} size="small" fullWidth />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField label="Valor decl." value={pkg.declared_value} onChange={(e) => updatePackageField(idx, "declared_value", e.target.value)} onFocus={() => setIsPackagesFocused(true)} onBlur={() => setIsPackagesFocused(false)} size="small" fullWidth />
                </Grid>
              </Grid>
            ))}
          {isPaqueteria && packages.length > 0 && !packagesValid && (
            <Typography variant="caption" color="error">
              Completá ancho, alto, profundidad y peso (mayores a 0) en cada bulto.
            </Typography>
          )}
        </Paper>
      </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "#f8fafc",
            },
          }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          ref={confirmButtonRef}
          onClick={() => onConfirm(buildPackagesPayload())}
          color="primary"
          disabled={loading || selectedProducts.length !== expandedDetails.length || !packagesValid}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          {loading ? "Confirmando..." : "Confirmar"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ModalPrepared;
